/**
 * The arrangement of one workspace (I-3641): a binary tree of tiles and splits,
 * and the rectangles it lays out. Pure, so every step is testable; a tile's
 * iframe must never move in the DOM (it would reload), so the view renders the
 * tiles as one flat list and places them with `layoutTree`.
 */

export type TileSide = "left" | "right" | "top" | "bottom";
/** Where a dragged tile lands on another: beside it, or `swap` places. */
export type TileDrop = TileSide | "swap";

export interface TileLeaf {
  kind: "tile";
  id: string;
  /** The route the tile shows; null while it offers the apps to pick from. */
  route: string | null;
  title?: string;
  /** The area of its app the running tile shows; stored like the title (I-3806). */
  area?: string;
}

export interface TileSplit {
  kind: "split";
  id: string;
  /** `row`: first left of second. `column`: first above second. */
  direction: "row" | "column";
  /** The first child's share of the split, 0–1. */
  ratio: number;
  first: TileNode;
  second: TileNode;
}

export type TileNode = TileLeaf | TileSplit;

export interface TileRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TileDivider {
  split: TileSplit;
  /** The whole split's rectangle; the line sits at `ratio` inside it. */
  rect: TileRect;
}

export interface TileLayout {
  tiles: { tile: TileLeaf; rect: TileRect }[];
  dividers: TileDivider[];
}

export const MIN_RATIO = 0.1;
export const MAX_RATIO = 0.9;

/** Every tile is a whole Connect in a frame, and a tab of many shows none of them well. */
export const MAX_TILES = 4;
/** What each half of a split keeps, in pixels: below it a tile is little more than its head. */
export const MIN_TILE_WIDTH = 320;
export const MIN_TILE_HEIGHT = 240;

export interface TileAreaSize {
  width: number;
  height: number;
}

export function clampRatio(ratio: number): number {
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));
}

/** Whether a new tile fits beside this one. Without a known `area` only the number counts. */
export function canSplitTile(
  root: TileNode,
  tileId: string,
  side: TileSide,
  area: TileAreaSize | null,
): boolean {
  const { tiles } = layoutTree(root);
  const placed = tiles.find(({ tile }) => tile.id === tileId);
  if (!placed || tiles.length >= MAX_TILES) return false;
  if (!area) return true;
  return side === "left" || side === "right"
    ? (placed.rect.width * area.width) / 2 >= MIN_TILE_WIDTH
    : (placed.rect.height * area.height) / 2 >= MIN_TILE_HEIGHT;
}

export function layoutTree(
  node: TileNode,
  rect: TileRect = { x: 0, y: 0, width: 1, height: 1 },
): TileLayout {
  if (node.kind === "tile")
    return { tiles: [{ tile: node, rect }], dividers: [] };
  const row = node.direction === "row";
  const firstRect: TileRect = row
    ? { ...rect, width: rect.width * node.ratio }
    : { ...rect, height: rect.height * node.ratio };
  const secondRect: TileRect = row
    ? {
        ...rect,
        x: rect.x + firstRect.width,
        width: rect.width - firstRect.width,
      }
    : {
        ...rect,
        y: rect.y + firstRect.height,
        height: rect.height - firstRect.height,
      };
  const first = layoutTree(node.first, firstRect);
  const second = layoutTree(node.second, secondRect);
  return {
    tiles: [...first.tiles, ...second.tiles],
    dividers: [{ split: node, rect }, ...first.dividers, ...second.dividers],
  };
}

export function listTiles(node: TileNode): TileLeaf[] {
  return node.kind === "tile"
    ? [node]
    : [...listTiles(node.first), ...listTiles(node.second)];
}

export function findTile(node: TileNode, id: string): TileLeaf | undefined {
  return listTiles(node).find((tile) => tile.id === id);
}

function mapNode(
  node: TileNode,
  change: (node: TileNode) => TileNode | null,
): TileNode | null {
  const own = change(node);
  if (own !== node || node.kind === "tile") return own;
  const first = mapNode(node.first, change);
  const second = mapNode(node.second, change);
  if (!first) return second;
  if (!second) return first;
  return first === node.first && second === node.second
    ? node
    : { ...node, first, second };
}

/** Put `tile` beside the tile `targetId`; an unknown target leaves the tree as it is. */
export function insertTile(
  root: TileNode,
  targetId: string,
  side: TileSide,
  tile: TileLeaf,
  splitId: string,
): TileNode {
  const next = mapNode(root, (node) => {
    if (node.kind !== "tile" || node.id !== targetId) return node;
    const before = side === "left" || side === "top";
    return {
      kind: "split",
      id: splitId,
      direction: side === "left" || side === "right" ? "row" : "column",
      ratio: 0.5,
      first: before ? tile : node,
      second: before ? node : tile,
    };
  });
  return next ?? root;
}

/** Null when the last tile went. The sibling takes the space of the closed tile. */
export function removeTile(root: TileNode, id: string): TileNode | null {
  return mapNode(root, (node) =>
    node.kind === "tile" && node.id === id ? null : node,
  );
}

export function patchTile(
  root: TileNode,
  id: string,
  patch: Partial<Omit<TileLeaf, "id" | "kind">>,
): TileNode {
  const next = mapNode(root, (node) =>
    node.kind === "tile" && node.id === id ? { ...node, ...patch } : node,
  );
  return next ?? root;
}

export function setSplitRatio(
  root: TileNode,
  splitId: string,
  ratio: number,
): TileNode {
  const change = (node: TileNode): TileNode => {
    if (node.kind === "tile") return node;
    if (node.id === splitId) return { ...node, ratio: clampRatio(ratio) };
    return { ...node, first: change(node.first), second: change(node.second) };
  };
  return change(root);
}

/** Move tile `id` onto `targetId`. Keeps both tiles' identity, so no iframe reloads. */
export function moveTile(
  root: TileNode,
  id: string,
  targetId: string,
  drop: TileDrop,
  splitId: string,
): TileNode {
  if (id === targetId) return root;
  const moved = findTile(root, id);
  const target = findTile(root, targetId);
  if (!moved || !target) return root;
  if (drop === "swap") {
    const change = (node: TileNode): TileNode => {
      if (node.kind === "tile")
        return node.id === id ? target : node.id === targetId ? moved : node;
      return {
        ...node,
        first: change(node.first),
        second: change(node.second),
      };
    };
    return change(root);
  }
  const without = removeTile(root, id);
  return without ? insertTile(without, targetId, drop, moved, splitId) : root;
}

/** The drop zone under a pointer, as shares of the tile: a cross with `swap` in its middle. */
export function dropAt(px: number, py: number): TileDrop {
  const inMiddle = (share: number) => share >= 0.3 && share <= 0.7;
  if (inMiddle(px) && inMiddle(py)) return "swap";
  if (Math.abs(px - 0.5) >= Math.abs(py - 0.5))
    return px < 0.5 ? "left" : "right";
  return py < 0.5 ? "top" : "bottom";
}

function isNode(value: unknown): value is TileNode {
  if (!value || typeof value !== "object") return false;
  const node = value as Record<string, unknown>;
  if (typeof node.id !== "string") return false;
  if (node.kind === "tile")
    return node.route == null || typeof node.route === "string";
  return (
    node.kind === "split" &&
    (node.direction === "row" || node.direction === "column") &&
    typeof node.ratio === "number" &&
    isNode(node.first) &&
    isNode(node.second)
  );
}

/** A stored arrangement, or null when it is not one (an older or broken value). */
export function readStoredTree(value: unknown): TileNode | null {
  if (!isNode(value)) return null;
  const change = (node: TileNode): TileNode =>
    node.kind === "tile"
      ? { ...node, route: node.route ?? null }
      : {
          ...node,
          ratio: clampRatio(node.ratio),
          first: change(node.first),
          second: change(node.second),
        };
  return change(value);
}
